import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { ToastsStore } from "react-toasts";
import { callApi } from "../../utils/api";
import ReplyQuery from "./ReplyQuery";

declare const $: any;

interface QueryTableClientGivenProps {
  fod_fk: string;
  getRepliedQueries?: (queries: any[]) => void;
  queryreason?: any[];
  setSelectedItem?: (item: any) => void;
  getDeviationQueryData?: (queryid: any, isChecked: boolean, data?: any) => void;
  pageDynamicList?: any[];
  isReplyRaiseQueryShown?: boolean;
  replyQueryList?: any[];
  onReplyQueryChange?: (e: any, replyIndex: number) => void;
  onReplyDetailsChange?: (value: any, key: string, replyIndex: number) => void;
  onReplyRemove?: (replyIndex: number) => void;
  onReplyAdd?: () => void;
}

type QueryFilterType = "all" | "pending" | "queryReceived" | "completed";

const isCompletedStatus = (status: unknown) => String(status) === "3";
const isQueryReceivedStatus = (status: unknown) => String(status) === "2";

const safeText = (value: unknown) => String(value || "").trim();

/** Checkbox / submit payloads: never send a numeric id or invalid value as created_date. */
const formatCreatedDateForQueryPayload = (raw: unknown, sourceQueryId?: unknown) => {
  const sid = safeText(sourceQueryId);
  const s = raw != null && raw !== "" ? String(raw).trim() : "";
  if (!s) return moment().format("DD-MM-YYYY HH:mm:ss");
  if (/^\d{1,9}$/.test(s)) return moment().format("DD-MM-YYYY HH:mm:ss");
  if (sid && s === sid) return moment().format("DD-MM-YYYY HH:mm:ss");
  const parsed = moment(s, ["YYYY-MM-DD HH:mm:ss", "DD-MM-YYYY HH:mm:ss", "DD-MM-YYYY HH:mm:ss.SSS", moment.ISO_8601], true);
  if (!parsed.isValid()) return moment().format("DD-MM-YYYY HH:mm:ss");
  return parsed.format("DD-MM-YYYY HH:mm:ss");
};

const formatDate = (value: any) => {
  if (!value) return "-";
  const parsed = moment(value, ["YYYY-MM-DD HH:mm:ss", "DD-MM-YYYY HH:mm:ss", "DD-MM-YYYY HH:mm:ss.SSS", moment.ISO_8601], true);
  if (parsed.isValid()) return parsed.format("DD-MM-YYYY");
  const fallback = moment(value);
  return fallback.isValid() ? fallback.format("DD-MM-YYYY") : "-";
};

const formatTime = (value: any) => {
  if (!value) return "-";
  const parsed = moment(value, ["YYYY-MM-DD HH:mm:ss", "DD-MM-YYYY HH:mm:ss", "DD-MM-YYYY HH:mm:ss.SSS", moment.ISO_8601], true);
  if (parsed.isValid()) return parsed.format("hh:mm A");
  const fallback = moment(value);
  return fallback.isValid() ? fallback.format("hh:mm A") : "-";
};

const timeValue = (value: any) => {
  if (!value) return 0;
  const parsed = moment(value, ["YYYY-MM-DD HH:mm:ss", "DD-MM-YYYY HH:mm:ss", "DD-MM-YYYY HH:mm:ss.SSS", moment.ISO_8601], true);
  if (parsed.isValid()) return parsed.valueOf();
  const fallback = moment(value);
  return fallback.isValid() ? fallback.valueOf() : 0;
};

const sortTimelineLatestFirst = (timeline: any[]) => [...(Array.isArray(timeline) ? timeline : [])].sort((a, b) => timeValue(b) - timeValue(a));

const sortTimelineEntriesLatestFirst = (entries: any[]) =>
  [...(Array.isArray(entries) ? entries : [])].sort((a, b) => timeValue(b?.raisedDate) - timeValue(a?.raisedDate));

const mergeTimelineEntries = (existing: any[] = [], incoming: any[] = []) => {
  const merged = [...existing, ...incoming];
  const seen = new Set<string>();
  return merged.filter((entry) => {
    const raisedDate = safeText(entry?.raisedDate);
    if (!raisedDate) return false;
    const key = `${safeText(entry?.sourceId)}|${raisedDate}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const enrichTimelineEntryReceived = (
  entry: any,
  docMap: Record<string, any[]>,
  docCreatedByMap: Record<string, string>,
  hintDateMap: Record<string, string[]>,
  hintCreatedByMap: Record<string, string>
) => {
  const sid = safeText(entry?.sourceId);
  let receivedDate = entry?.receivedDate || "";
  let receivedBy = entry?.receivedBy || "";
  const docs = docMap[sid] || [];
  const hintDates = hintDateMap[sid] || [];

  if (!receivedDate && docs.length) {
    const sortedDocs = [...docs].sort((a: any, b: any) => timeValue(b?.created_date) - timeValue(a?.created_date));
    receivedDate = sortedDocs[0]?.created_date || "";
    receivedBy = receivedBy || docCreatedByMap[sid] || "";
  }
  if (!receivedDate && hintDates.length) {
    const sortedHints = sortTimelineLatestFirst(hintDates);
    receivedDate = sortedHints[0] || "";
    receivedBy = receivedBy || hintCreatedByMap[sid] || "";
  }

  return { ...entry, receivedDate, receivedBy };
};

const createReplyDraft = () => ({
  query: "",
  location: "",
  review: "",
  other_review: "",
  created_date: moment().format("DD-MM-YYYY HH:mm:ss"),
  filter_date: moment().format("DD-MM-YYYY HH:mm:ss.SSS"),
});

const isReplyDraftEmpty = (reply: any) => {
  if (!reply) return true;
  return !safeText(reply.query) && !safeText(reply.location) && !safeText(reply.review) && !safeText(reply.other_review);
};

const QueryTableComponent = ({
  fod_fk,
  getRepliedQueries,
  queryreason = [],
  setSelectedItem,
  getDeviationQueryData,
  pageDynamicList = [],
  isReplyRaiseQueryShown = false,
  replyQueryList = [],
  onReplyQueryChange,
  onReplyDetailsChange,
  onReplyRemove,
  onReplyAdd,
}: QueryTableClientGivenProps) => {
  const hasRequestDeviationAccess = Array.isArray(pageDynamicList)
    ? pageDynamicList.some((page: any) => page?.page_name === "Request for Deviation" && page?.status == true)
    : false;

  const [treeList, setTreeList] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState<QueryFilterType>("pending");
  const [searchText, setSearchText] = useState("");
  const [reasonFilter, setReasonFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [expandedRowId, setExpandedRowId] = useState<string>("");
  const [activeRequeryIds, setActiveRequeryIds] = useState<string[]>([]);
  const [checkedRowIds, setCheckedRowIds] = useState<string[]>([]);
  const [queryToComplete, setQueryToComplete] = useState<any>(null);
  const [isCompletingQuery, setIsCompletingQuery] = useState(false);
  const [previewDoc, setPreviewDoc] = useState("");
  const [queryHintMap, setQueryHintMap] = useState<Record<string, string[]>>({});
  const [queryHintDateMap, setQueryHintDateMap] = useState<Record<string, string[]>>({});
  const [queryHintCreatedByMap, setQueryHintCreatedByMap] = useState<Record<string, string>>({});
  const [queryReceivedDocMap, setQueryReceivedDocMap] = useState<Record<string, any[]>>({});
  const [queryReceivedDocCreatedByMap, setQueryReceivedDocCreatedByMap] = useState<Record<string, string>>({});

  const fetchQueryHints = () => {
    callApi("POST", "query_hint_list.php", { file_id: fod_fk })
      .then((res) => res.data)
      .then((response) => {
        const hintMap: Record<string, string[]> = {};
        const hintDateMap: Record<string, string[]> = {};
        const hintCreatedByMap: Record<string, string> = {};
        if (response?.result && Array.isArray(response.data)) {
          response.data.forEach((item: any) => {
            if (item?.query_id) {
              const key = String(item.query_id);
              if (!Array.isArray(hintMap[key])) hintMap[key] = [];
              if (!Array.isArray(hintDateMap[key])) hintDateMap[key] = [];
              if (item?.hint && String(item.hint).trim()) {
                hintMap[key].push(item.hint);
              }
              const hintDate = item?.updated_date || item?.created_date || "";
              if (hintDate) {
                hintDateMap[key].push(hintDate);
              }
              if (!hintCreatedByMap[key]) {
                const createdBy = safeText(item?.created_by);
                if (createdBy) hintCreatedByMap[key] = createdBy;
              }
            }
          });
        }
        setQueryHintMap(hintMap);
        setQueryHintDateMap(hintDateMap);
        setQueryHintCreatedByMap(hintCreatedByMap);
      })
      .catch(() => {
        setQueryHintMap({});
        setQueryHintDateMap({});
        setQueryHintCreatedByMap({});
      });
  };

  const fetchQueryReceivedDocs = () => {
    callApi("POST", "query_received_docs_get.php", { file_id: fod_fk })
      .then((res) => res.data)
      .then((response) => {
        const docMap: Record<string, any[]> = {};
        const docCreatedByMap: Record<string, string> = {};
        if (response?.result && Array.isArray(response.data)) {
          response.data.forEach((item: any) => {
            const key = String(item?.query_id || "");
            if (!key) return;
            if (!Array.isArray(docMap[key])) docMap[key] = [];
            docMap[key].push(item);
            if (!docCreatedByMap[key]) {
              const createdBy = safeText(item?.created_by);
              if (createdBy) docCreatedByMap[key] = createdBy;
            }
          });
        }
        setQueryReceivedDocMap(docMap);
        setQueryReceivedDocCreatedByMap(docCreatedByMap);
      })
      .catch(() => {
        setQueryReceivedDocMap({});
        setQueryReceivedDocCreatedByMap({});
      });
  };

  const fetchQueryTree = () => {
    callApi("POST", "query_tree.php", { fod_fk })
      .then((res) => res.data)
      .then((response) => setTreeList(Array.isArray(response) ? response : []))
      .catch(() => setTreeList([]));
  };

  useEffect(() => {
    fetchQueryTree();
  }, [fod_fk]);

  useEffect(() => {
    fetchQueryHints();
    fetchQueryReceivedDocs();
    const onQueryDataUpdated = () => {
      fetchQueryTree();
      fetchQueryHints();
      fetchQueryReceivedDocs();
    };
    window.addEventListener("query-hint-updated", onQueryDataUpdated);
    window.addEventListener("query-tray-saved", onQueryDataUpdated);
    return () => {
      window.removeEventListener("query-hint-updated", onQueryDataUpdated);
      window.removeEventListener("query-tray-saved", onQueryDataUpdated);
    };
  }, [fod_fk]);

  const rows = useMemo(() => {
    const groupedRows = new Map<string, any>();

    const getReceivedBy = (details: any, node: any) => {
      const raw = details?.query_received_by ?? details?.received_by ?? node?.query_received_by ?? node?.received_by ?? "";
      if (typeof raw === "string") return raw.trim();
      return safeText(raw?.name || raw?.username || raw?.first_name);
    };

    const getRaisedBy = (details: any, node: any) => {
      const raw =
        details?.raised_by ??
        details?.raice_by ??
        details?.raise_by ??
        details?.created_by ??
        node?.raised_by ??
        node?.raice_by ??
        node?.raise_by ??
        node?.created_by ??
        "";
      if (typeof raw === "string") return raw.trim();
      return safeText(raw?.name || raw?.username || raw?.first_name);
    };

    const visit = (node: any, indexPath: string) => {
      const details = node?.details || {};
      const replies = Array.isArray(node?.replies) ? node.replies : [];
      const nodeId = safeText(node?.id);
      const title = safeText(details?.title || node?.title);
      const reason = safeText(details?.reason || node?.reason);
      const location = safeText(details?.location || node?.location) || "-";
      const status = safeText(details?.status || node?.status);
      const createdDate = details?.created_date || node?.created_date || "";
      const receivedDate = details?.query_received_date || details?.received_date || node?.query_received_date || node?.received_date || "";
      const receivedBy = getReceivedBy(details, node) || queryReceivedDocCreatedByMap[nodeId] || queryHintCreatedByMap[nodeId] || "";
      const raisedBy = getRaisedBy(details, node);
      const completedBy = safeText(details?.query_completed_by || node?.query_completed_by);
      const deviationStatus = safeText(details?.deviation_status ?? node?.deviation_status ?? "0");
      const hasUploadedReceivedDoc = Array.isArray(queryReceivedDocMap[nodeId]) && queryReceivedDocMap[nodeId].length > 0;
      const hasQueryHint = Array.isArray(queryHintMap[nodeId]) && queryHintMap[nodeId].length > 0;
      const isQueryReceived = Boolean(isQueryReceivedStatus(status) || receivedDate || receivedBy || hasUploadedReceivedDoc || hasQueryHint);
      // Group same query and always show latest query details.
      const groupKey = `${title.toLowerCase()}|${reason.toLowerCase()}|${location.toLowerCase()}`;
      const replyRaisedDates = replies.map((reply: any) => reply?.created_date).filter((value: any) => Boolean(value));
      const replyReceivedDates = replies
        .map((reply: any) => reply?.query_received_date || reply?.received_date || reply?.receivedDate || "")
        .filter((value: any) => Boolean(value));
      const timelineEntries: any[] = [];
      if (createdDate) {
        timelineEntries.push({
          sourceId: nodeId,
          raisedDate: createdDate,
          receivedDate,
          raisedBy,
          receivedBy,
          completedBy,
        });
      }
      replies.forEach((reply: any) => {
        const replyRaisedDate = reply?.created_date || reply?.filter_date || "";
        if (!replyRaisedDate) return;
        timelineEntries.push({
          sourceId: safeText(reply?.query_id || reply?.id || nodeId),
          raisedDate: replyRaisedDate,
          receivedDate: reply?.query_received_date || reply?.received_date || reply?.receivedDate || "",
          raisedBy: getRaisedBy(reply, details) || getRaisedBy(reply, node) || raisedBy,
          receivedBy: getReceivedBy(reply, details) || getReceivedBy(reply, node) || receivedBy,
          completedBy: safeText(reply?.query_completed_by) || completedBy,
        });
      });
      const raisedDateTimeline = timelineEntries.map((entry: any) => entry.raisedDate);
      const receivedDateTimeline = timelineEntries
        .map((entry: any) => entry.receivedDate)
        .filter((value: any) => Boolean(value));

      if (!groupedRows.has(groupKey)) {
        groupedRows.set(groupKey, {
          id: safeText(node?.id || `${indexPath}-${title}`),
          sourceId: safeText(node?.id),
          queryNo: indexPath,
          title,
          reason,
          location,
          status,
          createdDate,
          receivedDate,
          receivedBy,
          raisedBy,
          completedBy,
          isQueryReceived,
          deviationStatus,
          nodeData: node,
          replies,
          raisedCount: timelineEntries.length || 1,
          raisedDateTimeline,
          receivedDateTimeline,
          timelineEntries,
        });
      } else {
        const existing = groupedRows.get(groupKey);
        existing.timelineEntries = mergeTimelineEntries(existing.timelineEntries || [], timelineEntries);
        existing.raisedDateTimeline = existing.timelineEntries.map((entry: any) => entry.raisedDate);
        existing.receivedDateTimeline = existing.timelineEntries
          .map((entry: any) => entry.receivedDate)
          .filter((value: any) => Boolean(value));
        existing.raisedCount = existing.timelineEntries.length || 1;
        existing.replies = [...(existing.replies || []), ...replies];

        const currentCreatedTime = timeValue(createdDate);
        const existingCreatedTime = timeValue(existing.createdDate);

        // Always show the latest query details in grouped row.
        if (currentCreatedTime >= existingCreatedTime) {
          existing.createdDate = createdDate;
          existing.title = title || existing.title;
          existing.status = status || existing.status;
          existing.sourceId = safeText(node?.id) || existing.sourceId;
          existing.queryNo = indexPath;
          existing.isQueryReceived = isQueryReceived;
          existing.nodeData = node;
          existing.deviationStatus = deviationStatus || existing.deviationStatus;
          if (receivedDate) {
            existing.receivedDate = receivedDate;
          }
          if (receivedBy) {
            existing.receivedBy = receivedBy;
          }
          if (raisedBy) {
            existing.raisedBy = raisedBy;
          }
          if (completedBy) {
            existing.completedBy = completedBy;
          }
        }

        if (timeValue(receivedDate) > timeValue(existing.receivedDate)) {
          existing.receivedDate = receivedDate;
        }
        if (!existing.receivedBy && receivedBy) {
          existing.receivedBy = receivedBy;
        }
        if (!existing.raisedBy && raisedBy) {
          existing.raisedBy = raisedBy;
        }
        if (!existing.completedBy && completedBy) {
          existing.completedBy = completedBy;
        }
        if (isQueryReceived) {
          existing.isQueryReceived = true;
        }
        if (isQueryReceivedStatus(status)) {
          existing.status = "2";
        }
      }

      (node?.children || []).forEach((child: any, index: number) => visit(child, `${indexPath}.${index + 1}`));
    };

    treeList.forEach((root: any, index: number) => visit(root, `${index + 1}`));
    return Array.from(groupedRows.values()).sort((a: any, b: any) => {
      const createdCompare = timeValue(a.createdDate) - timeValue(b.createdDate);
      if (createdCompare !== 0) return createdCompare;
      const sourceCompare = safeText(a.sourceId).localeCompare(safeText(b.sourceId), undefined, { numeric: true, sensitivity: "base" });
      if (sourceCompare !== 0) return sourceCompare;
      return safeText(a.title).localeCompare(safeText(b.title));
    });
  }, [treeList, queryReceivedDocMap, queryHintMap, queryReceivedDocCreatedByMap, queryHintCreatedByMap]);

  const statusCounts = useMemo(() => {
    const queryReceived = rows.filter((row: any) => !isCompletedStatus(row.status) && (isQueryReceivedStatus(row.status) || row.isQueryReceived)).length;
    const pending = rows.filter((row: any) => !isCompletedStatus(row.status)).length;
    const completed = rows.filter((row: any) => isCompletedStatus(row.status)).length;
    return { all: rows.length, pending, queryReceived, completed };
  }, [rows]);

  const reasons = useMemo(() => {
    const lookup = new Set<string>();
    rows.forEach((row: any) => {
      if (row.reason) lookup.add(row.reason);
    });
    return Array.from(lookup.values());
  }, [rows]);

  const filteredRows = useMemo(() => {
    const text = searchText.trim().toLowerCase();
    return rows.filter((row: any) => {
      const matchesText =
        !text ||
        row.title.toLowerCase().includes(text) ||
        row.reason.toLowerCase().includes(text) ||
        row.location.toLowerCase().includes(text);
      const matchesReason = reasonFilter === "all" || row.reason === reasonFilter;

      const rowDate = moment(row.createdDate, ["YYYY-MM-DD HH:mm:ss", "DD-MM-YYYY HH:mm:ss", moment.ISO_8601], true);
      const fromOk = !dateFrom || (rowDate.isValid() && rowDate.isSameOrAfter(moment(dateFrom), "day"));
      const toOk = !dateTo || (rowDate.isValid() && rowDate.isSameOrBefore(moment(dateTo), "day"));

      if (!matchesText || !matchesReason || !fromOk || !toOk) return false;
      // Pending tab should include both Pending (0) and Query Received (2) rows.
      if (statusFilter === "pending") return !isCompletedStatus(row.status);
      if (statusFilter === "queryReceived") return !isCompletedStatus(row.status) && (isQueryReceivedStatus(row.status) || row.isQueryReceived);
      if (statusFilter === "completed") return isCompletedStatus(row.status);
      return true;
    });
  }, [rows, searchText, reasonFilter, dateFrom, dateTo, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  const pageNumbers = useMemo(() => Array.from({ length: totalPages }, (_, index) => index + 1), [totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, reasonFilter, dateFrom, dateTo, statusFilter, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const syncRepliesToTree = (sourceId: string, replies: any[]) => {
    if (!sourceId) return;
    const applyReplies = (nodes: any[]): any[] =>
      nodes.map((node) => {
        const nodeId = safeText(node?.id);
        if (nodeId === sourceId) return { ...node, replies };
        if (node?.children?.length) return { ...node, children: applyReplies(node.children) };
        return node;
      });
    setTreeList((prev) => {
      const next = applyReplies(prev);
      if (getRepliedQueries) getRepliedQueries(next);
      return next;
    });
  };

  const getStatusLabel = (row: any) => {
    if (isCompletedStatus(row.status)) return "Completed";
    if (row.isQueryReceived) return "Query Received";
    return "Pending";
  };

  const getStatusClass = (row: any) => {
    if (isCompletedStatus(row.status)) return "query-client-pill query-client-pill-complete";
    if (row.isQueryReceived) return "query-client-pill query-client-pill-received";
    return "query-client-pill query-client-pill-pending";
  };

  const clearFilters = () => {
    setSearchText("");
    setReasonFilter("all");
    setDateFrom("");
    setDateTo("");
    setStatusFilter("all");
  };

  const getQueryReasonCopyText = (row: any) => {
    const queryText = safeText(row?.title) || "-";
    const reasonText = safeText(row?.reason) || "-";
    return `Query: ${queryText}\nReason: ${reasonText}`;
  };

  const copyQueryAndReason = async (row: any) => {
    const content = getQueryReasonCopyText(row);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(content);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = content;
        textarea.setAttribute("readonly", "true");
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      ToastsStore.success("Query and reason copied");
    } catch (error) {
      // Keep UI non-blocking if clipboard access is not available.
      console.error("Unable to copy query and reason", error);
      ToastsStore.error("Unable to copy query and reason");
    }
  };

  const copyAllPendingQueriesWithSerial = async () => {
    if (statusFilter !== "pending") return;
    const content = filteredRows.length
      ? filteredRows
        .map((row: any, index: number) => {
          const queryText = safeText(row?.title) || "-";
          const reasonText = safeText(row?.reason) || "-";
          return `${index + 1}. Query: ${queryText}\n   Reason: ${reasonText}`;
        })
        .join("\n\n")
      : "No queries available";
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(content);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = content;
        textarea.setAttribute("readonly", "true");
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      ToastsStore.success("Pending queries copied");
    } catch (error) {
      console.error("Unable to copy pending queries", error);
      ToastsStore.error("Unable to copy pending queries");
    }
  };

  const buildSelectedItemPayload = (row: any) => ({
    query: row?.title || "",
    review: row?.reason || "",
    query_id: row?.sourceId || "",
    location: row?.location || "",
    created_date: row?.sourceId
      ? moment().format("DD-MM-YYYY HH:mm:ss")
      : formatCreatedDateForQueryPayload(row?.createdDate, row?.sourceId),
    filter_date: moment().format("DD-MM-YYYY HH:mm:ss.SSS"),
  });

  const handleRowCheckboxToggle = (row: any, checked: boolean) => {
    const rowId = safeText(row?.id);
    if (!rowId) return;
    setCheckedRowIds((prev) => (checked ? [...prev, rowId] : prev.filter((id) => id !== rowId)));
    if (checked) {
      setActiveRequeryIds((prev) => prev.filter((id) => id !== rowId));
    }
    if (setSelectedItem) {
      setSelectedItem(buildSelectedItemPayload(row));
    }
    if (getDeviationQueryData) {
      getDeviationQueryData(row?.sourceId || rowId, checked, row?.nodeData);
    }
  };

  const closeQuery = () => {
    if (!queryToComplete?.sourceId) return;
    setIsCompletingQuery(true);
    const user = localStorage.getItem("user");
    const userId = user ? JSON.parse(user)["userId"] : "";
    callApi("POST", "query_close.php", { id: queryToComplete.sourceId, status: 3, userId })
      .then((res) => res.data)
      .then(() => {
        setQueryToComplete(null);
        fetchQueryTree();
      })
      .catch(() => {
        setQueryToComplete(null);
        fetchQueryTree();
      })
      .finally(() => setIsCompletingQuery(false));
  };

  const requestDeviation = (row: any) => {
    const queryData = row?.nodeData || {
      id: row?.sourceId || row?.id,
      details: { deviation_status: String(row?.deviationStatus || "0") },
    };
    const queryId = queryData?.id ? String(queryData.id) : String(row?.sourceId || row?.id || "");
    if (getDeviationQueryData && queryId) {
      getDeviationQueryData(queryId, true, queryData);
    }
    if (typeof $ === "function") {
      $("#deviationModal").modal("show");
    }
  };

  const addReply = (row: any) => {
    const existing = Array.isArray(row.replies) ? row.replies : [];
    syncRepliesToTree(row.sourceId, [...existing, createReplyDraft()]);
  };

  const removeReply = (row: any, index: number) => {
    const existing = Array.isArray(row.replies) ? [...row.replies] : [];
    existing.splice(index, 1);
    syncRepliesToTree(row.sourceId, existing);
    if (existing.length === 0) {
      setActiveRequeryIds((prev) => prev.filter((id) => id !== row.id));
    }
  };

  const handleReplyChange = (row: any, e: any, index: number) => {
    const existing = Array.isArray(row.replies) ? [...row.replies] : [];
    if (!existing[index]) return;
    existing[index][e.target.name] = e.target.value;
    syncRepliesToTree(row.sourceId, existing);
  };

  const handleReplyDetailsChange = (row: any, value: any, key: string, index: number) => {
    const existing = Array.isArray(row.replies) ? [...row.replies] : [];
    if (!existing[index]) return;
    existing[index][key] = value;
    syncRepliesToTree(row.sourceId, existing);
  };

  const lightActionButtonStyle = {
    minHeight: "30px",
    padding: "4px 12px",
    borderRadius: "8px",
    border: "1px solid #cfe0f2",
    background: "#f4f9ff",
    color: "#285a91",
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: 1.2,
    whiteSpace: "nowrap" as const,
  };

  const lightCompleteActionButtonStyle = {
    ...lightActionButtonStyle,
    border: "1px solid #bfe4cd",
    background: "#e8f6ec",
    color: "#2a7a4b",
  };

  const lightIconButtonStyle = {
    width: "30px",
    height: "30px",
    borderRadius: "8px",
    border: "1px solid #d8e4f0",
    background: "#f8fbff",
    color: "#5b6b7d",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
  };

  return (
    <div className="query-client-given-wrapper">
      <div className="query-client-tabs">
        <button type="button" className={`tab-all ${statusFilter === "all" ? "active" : ""}`} onClick={() => setStatusFilter("all")}>All Queries <span>{statusCounts.all}</span></button>
        <button type="button" className={`tab-pending ${statusFilter === "pending" ? "active" : ""}`} onClick={() => setStatusFilter("pending")}>Pending <span>{statusCounts.pending}</span></button>
        <button type="button" className={`tab-received ${statusFilter === "queryReceived" ? "active" : ""}`} onClick={() => setStatusFilter("queryReceived")}>Query Received <span>{statusCounts.queryReceived}</span></button>
        <button type="button" className={`tab-completed ${statusFilter === "completed" ? "active" : ""}`} onClick={() => setStatusFilter("completed")}>Completed <span>{statusCounts.completed}</span></button>
      </div>

      <div className="query-client-filters">
        <div className="query-client-field">
          <i className="fa fa-search" aria-hidden="true"></i>
          <input type="text" placeholder="Search query..." value={searchText} onChange={(e) => setSearchText(e.target.value)} />
        </div>
        <select value={reasonFilter} onChange={(e) => setReasonFilter(e.target.value)}>
          <option value="all">All Reasons</option>
          {reasons.map((reason) => (
            <option key={reason} value={reason}>{reason}</option>
          ))}
        </select>
        <div className="query-client-date-range">
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          <span>to</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        {statusFilter === "pending" ? (
          <button type="button" className="query-client-reset-btn" onClick={() => { void copyAllPendingQueriesWithSerial(); }}>
            Copy All Queries
          </button>
        ) : null}
        <button type="button" className="query-client-reset-btn" onClick={clearFilters}>Reset</button>
      </div>

      <div className="table-responsive">
        <table className="table query-client-given-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={paginatedRows.length > 0 && paginatedRows.every((row: any) => checkedRowIds.includes(row.id))}
                  onChange={(e) => {
                    const shouldCheck = e.target.checked;
                    paginatedRows.forEach((row: any) => {
                      const rowAlreadyChecked = checkedRowIds.includes(row.id);
                      if (shouldCheck && !rowAlreadyChecked) handleRowCheckboxToggle(row, true);
                      if (!shouldCheck && rowAlreadyChecked) handleRowCheckboxToggle(row, false);
                    });
                  }}
                />
              </th>
              <th>SL</th>
              <th></th>
              <th>Query</th>
              <th>Reason</th>
              <th>Count</th>
              <th>Raised On</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedRows.length ? (
              paginatedRows.map((row: any, index: number) => {
                const absoluteIndex = (currentPage - 1) * pageSize + index + 1;
                const isExpanded = expandedRowId === row.id;
                const isRequeryOpen = activeRequeryIds.includes(row.id);
                const isRowChecked = checkedRowIds.includes(row.id);
                const sortedTimelineEntries = sortTimelineEntriesLatestFirst(
                  (row.timelineEntries || []).map((entry: any) =>
                    enrichTimelineEntryReceived(
                      entry,
                      queryReceivedDocMap,
                      queryReceivedDocCreatedByMap,
                      queryHintDateMap,
                      queryHintCreatedByMap
                    )
                  )
                );
                const maxTimelineRows = sortedTimelineEntries.length;
                const rowUploadedDocs = [...(queryReceivedDocMap[String(row.sourceId || row.id)] || [])].sort(
                  (a: any, b: any) => timeValue(b?.created_date) - timeValue(a?.created_date)
                );
                const latestUploadedDoc = rowUploadedDocs[0] || null;
                const isPendingQuery = !isCompletedStatus(row.status) && !(isQueryReceivedStatus(row.status) || row.isQueryReceived);
                const canRequestDeviation = hasRequestDeviationAccess && !isCompletedStatus(row.status) && !["1", "2"].includes(String(row.deviationStatus || "0"));
                const canCompleteQuery = !isCompletedStatus(row.status) && (isPendingQuery || isQueryReceivedStatus(row.status) || row.isQueryReceived);
                const shouldShowReceivedBy = isCompletedStatus(row.status) || isQueryReceivedStatus(row.status) || row.isQueryReceived;
                return (
                  <React.Fragment key={row.id}>
                    <tr
                      className="query-client-row"
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={checkedRowIds.includes(row.id)}
                          disabled={isCompletedStatus(row.status)}
                          onChange={(e) => handleRowCheckboxToggle(row, e.target.checked)}
                        />
                      </td>
                      <td>{absoluteIndex}</td>
                      <td>
                        <span
                          className={`query-row-dot ${isCompletedStatus(row.status)
                            ? "dot-completed"
                            : (isQueryReceivedStatus(row.status) || row.isQueryReceived)
                              ? "dot-received"
                              : "dot-pending"
                            }`}
                        ></span>
                      </td>
                      <td>
                        <div className="query-client-main">{row.title || "-"}</div>
                        <div className="query-client-sub">{row.location || "-"}</div>
                      </td>
                      <td>{row.reason || "-"}</td>
                      <td>{row.raisedCount || 1}</td>
                      <td>
                        <div className="query-client-main">{formatDate(row.createdDate)}</div>
                        <div className="query-client-sub">{formatTime(row.createdDate)}</div>
                      </td>
                      <td><span className={getStatusClass(row)}>{getStatusLabel(row)}</span></td>
                      <td>
                        <div className="query-client-actions" style={{ alignItems: "center", gap: "8px", flexWrap: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                          {rowUploadedDocs.length > 0 ? (
                            <button
                              type="button"
                              className="icon-btn"
                              style={lightIconButtonStyle}
                              title="View Uploaded Document"
                              onClick={() => setPreviewDoc(latestUploadedDoc?.doc_url || latestUploadedDoc?.base_url || "")}
                            >
                              <i className="fa fa-eye" aria-hidden="true"></i>
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="icon-btn"
                            style={lightIconButtonStyle}
                            title="Copy Query and Reason"
                            onClick={() => {
                              void copyQueryAndReason(row);
                            }}
                          >
                            <i className="fa fa-copy" aria-hidden="true"></i>
                          </button>

                          <button
                            type="button"
                            className="icon-btn"
                            style={lightIconButtonStyle}
                            title="Expand"
                            onClick={() => setExpandedRowId(isExpanded ? "" : row.id)}
                          >
                            <i className={`fa ${isExpanded ? "fa-angle-up" : "fa-angle-down"}`} aria-hidden="true"></i>
                          </button>
                          {!isCompletedStatus(row.status) && !isRowChecked ? (
                            <button
                              type="button"
                              className="query-client-requery-btn"
                              style={lightActionButtonStyle}
                              onClick={() => {
                                const isOpen = activeRequeryIds.includes(row.id);
                                if (!isOpen && (!Array.isArray(row.replies) || row.replies.length === 0)) {
                                  syncRepliesToTree(row.sourceId, [createReplyDraft()]);
                                }
                                if (isOpen && Array.isArray(row.replies) && row.replies.every((reply: any) => isReplyDraftEmpty(reply))) {
                                  // Closing untouched requery should not leave empty rows that trigger required validation on submit.
                                  syncRepliesToTree(row.sourceId, []);
                                }
                                setActiveRequeryIds((prev) => (isOpen ? prev.filter((id) => id !== row.id) : [...prev, row.id]));
                              }}
                            >
                              Requery
                            </button>
                          ) : null}
                          {canCompleteQuery ? (
                            <button
                              type="button"
                              className="query-client-requery-btn"
                              style={lightCompleteActionButtonStyle}
                              title="Complete Query"
                              onClick={() => setQueryToComplete(row)}
                            >
                              Complete
                            </button>
                          ) : null}
                          {canRequestDeviation ? (
                            <button
                              type="button"
                              className="query-client-requery-btn"
                              style={lightActionButtonStyle}
                              title="Request for Deviation"
                              onClick={() => requestDeviation(row)}
                            >
                              Request for Deviation
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>

                    {isExpanded ? (
                      <tr className="query-client-expanded-row">
                        <td colSpan={9}>
                          <div className="query-client-expanded">
                            <div className="query-client-expanded-timeline" style={{ display: "block", width: "100%" }}>
                              <table className="table query-client-given-table" style={{ marginBottom: 0, tableLayout: "fixed", width: "100%" }}>
                                <thead>
                                  <tr>
                                    <th style={{ width: "20%" }}>Raised Date &amp; Time</th>
                                    <th style={{ width: "20%" }}>Received Date &amp; Time</th>
                                    <th style={{ width: "20%" }}>Query Raised By</th>
                                    <th style={{ width: "20%" }}>Query Received By</th>
                                    <th style={{ width: "20%" }}>Query Completed By</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {maxTimelineRows > 0 ? (
                                    sortedTimelineEntries.map((entry: any, timelineIndex: number) => {
                                      const raisedValue = entry?.raisedDate || "";
                                      const receivedValue = entry?.receivedDate || "";
                                      const raisedText = raisedValue
                                        ? `${timelineIndex + 1}] ${formatDate(raisedValue)} ${formatTime(raisedValue)}`
                                        : "-";
                                      const receivedText = receivedValue
                                        ? `${timelineIndex + 1}] ${formatDate(receivedValue)} ${formatTime(receivedValue)}`
                                        : "-";
                                      const showReceivedBy =
                                        shouldShowReceivedBy && (Boolean(receivedValue) || Boolean(safeText(entry?.receivedBy)));
                                      return (
                                        <tr key={`${row.id}-timeline-${timelineIndex}`}>
                                          <td style={{ width: "20%" }}>{raisedText}</td>
                                          <td style={{ width: "20%" }}>{receivedText}</td>
                                          <td style={{ width: "20%" }}>{safeText(entry?.raisedBy) || "-"}</td>
                                          <td style={{ width: "20%" }}>{showReceivedBy ? (safeText(entry?.receivedBy) || "-") : "-"}</td>
                                          <td style={{ width: "20%" }}>
                                            {isCompletedStatus(row.status)
                                              ? (safeText(entry?.completedBy) || safeText(row?.completedBy) || "-")
                                              : "-"}
                                          </td>
                                        </tr>
                                      );
                                    })
                                  ) : (
                                    <tr>
                                      <td style={{ width: "20%" }}>-</td>
                                      <td style={{ width: "20%" }}>-</td>
                                      <td style={{ width: "20%" }}>{safeText(row?.raisedBy) || "-"}</td>
                                      <td style={{ width: "20%" }}>{shouldShowReceivedBy ? (safeText(row?.receivedBy) || "-") : "-"}</td>
                                      <td style={{ width: "20%" }}>{isCompletedStatus(row.status) ? (safeText(row?.completedBy) || "-") : "-"}</td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                            {queryHintMap[String(row.sourceId || row.id)]?.length ? (
                              <div style={{ marginTop: "12px", border: "1px solid #eef0f4", borderRadius: "10px", background: "#fcfdff", overflow: "hidden" }}>
                                <div style={{ padding: "8px 12px", background: "#f5f7fb", borderBottom: "1px solid #eef0f4", fontWeight: 600, color: "#4b5563" }}>
                                  Query Hints
                                </div>
                                <div style={{ padding: "8px 12px" }}>
                                  {(queryHintMap[String(row.sourceId || row.id)] || []).map((hint: string, hintIndex: number) => (
                                    <div
                                      key={`hint-${row.id}-${hintIndex}`}
                                      style={{ padding: "6px 10px", borderRadius: "8px", background: "#ffffff", border: "1px solid #edf1f6", marginBottom: hintIndex === queryHintMap[String(row.sourceId || row.id)].length - 1 ? 0 : "6px" }}
                                    >
                                      <span style={{ fontWeight: 600, marginRight: "6px", color: "#6b7280" }}>{hintIndex + 1}.</span>
                                      <span style={{ color: "#111827" }}>{hint}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : null}
                            {Array.isArray(queryReceivedDocMap[String(row.sourceId || row.id)]) &&
                              queryReceivedDocMap[String(row.sourceId || row.id)].length > 0 ? (
                              <div style={{ marginTop: "12px", border: "1px solid #e8edf5", borderRadius: "10px", background: "#fff", overflow: "hidden" }}>
                                <div style={{ padding: "8px 12px", background: "#f5f7fb", borderBottom: "1px solid #e8edf5", fontWeight: 600 }}>
                                  Uploaded Query Documents
                                </div>
                                <div style={{ padding: "8px 12px" }}>
                                  {(queryReceivedDocMap[String(row.sourceId || row.id)] || []).map((doc: any, docIndex: number) => (
                                    <div key={`query-doc-${row.id}-${doc?.id || docIndex}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", border: "1px solid #edf1f6", borderRadius: "8px", marginBottom: docIndex === queryReceivedDocMap[String(row.sourceId || row.id)].length - 1 ? 0 : "6px" }}>
                                      <div>
                                        <div style={{ fontWeight: 600, color: "#374151" }}>{docIndex + 1}. {doc?.doc_name || "-"}</div>
                                        <div style={{ fontSize: "12px", color: "#6b7280" }}>
                                          Query Received Time: {doc?.created_date ? moment(doc.created_date).format("DD-MM-YYYY hh:mm A") : "-"}
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        className="icon-btn"
                                        title="View"
                                        onClick={() => setPreviewDoc(doc?.doc_url || doc?.base_url || "")}
                                      >
                                        <i className="fa fa-eye" aria-hidden="true"></i>
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ) : null}

                    {isRequeryOpen && Array.isArray(row.replies) && row.replies.length > 0 ? (
                      <tr className="query-client-expanded-row">
                        <td colSpan={9}>
                          <div className="raise-query-style" style={{ marginTop: "8px", marginBottom: "4px" }}>
                            <ReplyQuery
                              reply=""
                              addReply={() => addReply(row)}
                              removeReply={(replyIndex: number) => removeReply(row, replyIndex)}
                              handleChange={(e: any, replyIndex: number) => handleReplyChange(row, e, replyIndex)}
                              handleDetailsChange={(value: any, key: string, replyIndex: number) => handleReplyDetailsChange(row, value, key, replyIndex)}
                              queryreason={queryreason}
                              addnewQueryList={Array.isArray(row.replies) ? row.replies : []}
                            />
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={9} className="text-center py-3">No queries available</td>
              </tr>
            )}
            {isReplyRaiseQueryShown && Array.isArray(replyQueryList) && replyQueryList.length > 0 ? (
              <tr className="query-client-expanded-row">
                <td colSpan={9}>
                  <div className="raise-query-style" style={{ marginTop: "8px", marginBottom: "4px" }}>
                    <ReplyQuery
                      reply=""
                      addReply={() => {
                        if (onReplyAdd) onReplyAdd();
                      }}
                      removeReply={(replyIndex: number) => {
                        if (onReplyRemove) onReplyRemove(replyIndex);
                      }}
                      handleChange={(e: any, replyIndex: number) => {
                        if (onReplyQueryChange) onReplyQueryChange(e, replyIndex);
                      }}
                      handleDetailsChange={(value: any, key: string, replyIndex: number) => {
                        if (onReplyDetailsChange) onReplyDetailsChange(value, key, replyIndex);
                      }}
                      queryreason={queryreason}
                      addnewQueryList={replyQueryList}
                    />
                  </div>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div className="query-client-pagination">
        <span>
          Showing {(filteredRows.length && (currentPage - 1) * pageSize + 1) || 0} to {Math.min(currentPage * pageSize, filteredRows.length)} of {filteredRows.length} entries
        </span>
        <div className="query-client-page-controls">
          <div className="query-client-page-limit">
            <label htmlFor="query-client-page-limit-select">Show</label>
            <select
              id="query-client-page-limit-select"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <button type="button" disabled={currentPage === 1} onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}>
            <i className="fa fa-angle-left" aria-hidden="true"></i>
          </button>
          {pageNumbers.map((pageNumber) => (
            <button
              key={pageNumber}
              type="button"
              onClick={() => setCurrentPage(pageNumber)}
              className={currentPage === pageNumber ? "active" : ""}
              style={{ minWidth: "32px" }}
            >
              {pageNumber}
            </button>
          ))}
          <button type="button" disabled={currentPage === totalPages} onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}>
            <i className="fa fa-angle-right" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {queryToComplete ? (
        <div className="dialog-modal" role="dialog" aria-hidden="true">
          <div className="dialog-modal-body" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Complete Query</h5>
                <button type="button" className="close" aria-label="Close" onClick={() => setQueryToComplete(null)}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p>{queryToComplete.title || "Are you sure you want to complete this query?"}</p>
                <div style={{ width: "100%", textAlignLast: "center" }}>
                  <button onClick={closeQuery} className="justify-content-center ml2 btn btn-success btnupload text-center" disabled={isCompletingQuery}>
                    {isCompletingQuery ? <i className="fa fa-spinner fa-spin"></i> : "Complete"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {previewDoc ? (
        <div className="dialog-modal" role="dialog" aria-hidden="true" onClick={() => setPreviewDoc("")}>
          <div className="dialog-modal-body" role="document" style={{ width: "95vw", maxWidth: "1400px" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-content" style={{ height: "90vh" }}>
              <div className="modal-header">
                <h5 className="modal-title">Document Preview</h5>
                <button type="button" className="close" aria-label="Close" onClick={() => setPreviewDoc("")}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ flex: 1 }}>
                <iframe title="query-document-preview" width="100%" height="100%" src={previewDoc} />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setPreviewDoc("")}>Close</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default QueryTableComponent;
